import React, { useState } from 'react'
import { X, Pencil } from 'lucide-react'
import { projectsApi, ProjectSystem, SplitProjectSystemError } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface Props {
  system: ProjectSystem
  onClose: () => void
  // Fired with the updated row; caller refreshes its list.
  onSaved: (updated: ProjectSystem) => void
}

// QA item 2 (2026-06-12): per-row Edit modal for the project systems
// list. All six descriptive fields editable; word_ra / split rows are
// editable too (delivers the backlogged function_name backfill for
// word_ra-created rows). Rename uniqueness is enforced server-side
// (409 NAME_TAKEN) and surfaces as an inline error on the name field.
const FIELDS: Array<{ key: keyof EditableFields; label: string; placeholder?: string }> = [
  { key: 'system_name',          label: 'System name' },
  { key: 'vendor_name_raw',      label: 'Vendor' },
  { key: 'function_name',        label: 'Function', placeholder: 'e.g. Navigational systems' },
  { key: 'system_description',   label: 'Description' },
  { key: 'type_approval_status', label: 'Type approval status' },
  { key: 'cbs_grade',            label: 'CBS grade', placeholder: 'e.g. III' },
]

type EditableFields = Pick<ProjectSystem,
  'system_name' | 'vendor_name_raw' | 'function_name' |
  'system_description' | 'type_approval_status' | 'cbs_grade'>

export const EditProjectSystemModal: React.FC<Props> = ({ system, onClose, onSaved }) => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [values, setValues] = useState<Record<string, string>>(() => ({
    system_name:          system.system_name ?? '',
    vendor_name_raw:      system.vendor_name_raw ?? '',
    function_name:        system.function_name ?? '',
    system_description:   system.system_description ?? '',
    type_approval_status: system.type_approval_status ?? '',
    cbs_grade:            system.cbs_grade ?? '',
  }))
  const [busy, setBusy] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  const handleSave = async () => {
    setBusy(true)
    setNameError(null)
    try {
      // Send only changed fields so the audit diff stays tight.
      const changed: Partial<EditableFields> = {}
      for (const { key } of FIELDS) {
        const next = values[key].trim() || null
        const prev = (system[key] ?? null) as string | null
        if (next !== prev) (changed as Record<string, string | null>)[key] = next
      }
      if (Object.keys(changed).length === 0) {
        onClose()
        return
      }
      const updated = await projectsApi.updateProjectSystem(system.id, changed)
      success(t('editModal.systemUpdated'))
      onSaved(updated)
    } catch (err) {
      const e = err as SplitProjectSystemError
      if (e.code === 'NAME_TAKEN') {
        setNameError(e.message)
        setBusy(false)
        return
      }
      showError(e.message ?? t('editModal.updateFailed'))
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Pencil className="h-4 w-4 text-blue-500" />
            {t('systems.editSystem')}
            {system.source !== 'excel' && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-normal">
                {system.source === 'word_ra' ? 'Word RA' : 'Split'}
              </span>
            )}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" aria-label={t('common.close')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm max-h-[70vh] overflow-y-auto">
          {FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t(`editModal.fields.${key}`, { defaultValue: label })}
              </label>
              <input
                value={values[key]}
                onChange={e => {
                  setValues(prev => ({ ...prev, [key]: e.target.value }))
                  if (key === 'system_name') setNameError(null)
                }}
                placeholder={placeholder}
                className={`w-full text-sm px-3 py-2 rounded border bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                  ${key === 'system_name' && nameError
                    ? 'border-rose-400 dark:border-rose-500'
                    : 'border-slate-300 dark:border-slate-600'}`}
              />
              {key === 'system_name' && nameError && (
                <p className="text-[11px] text-rose-500 mt-1">{nameError}</p>
              )}
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button size="sm" onClick={handleSave} disabled={busy}>
            {busy ? t('wordRa.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
